import { Input, Textarea } from '@/components/ui/FormControls';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { refreshSaleConsumers } from '@/lib/refreshSaleConsumers';
import { useAuth } from '@/contexts/AuthContext';
import {
  usePurchasePayments,
  usePurchasePeople,
  usePurchaseOperation,
  type PurchaseDetail,
  type PurchasePayment,
} from '@/hooks/usePurchases';
import { usePurchaseIntent } from '@/hooks/usePurchaseIntent';
import { useConfirm } from '@/contexts/ConfirmContext';
import WorkModalShell from '@/components/common/WorkModalShell';
import ReceiptPreviewModal from '@/components/common/ReceiptPreviewModal';
import PurchasePaymentFields, {
  checkedPurchasePayment,
  emptyPurchasePayment,
  purchaseInputClass,
} from './PurchasePaymentFields';
import { saleMoney, saleStatusLabel } from '@klothiq/domain';
import { toast } from 'sonner';

export default function PurchasePayments({
  purchase,
  enabled,
}: {
  purchase: PurchaseDetail;
  enabled: boolean;
}) {
  const client = useQueryClient();
  const { can } = useAuth();
  const payments = usePurchasePayments(purchase.id);
  const people = usePurchasePeople(enabled);
  const confirm = useConfirm();
  const intent = usePurchaseIntent(purchase.id);
  const [editor, setEditor] = useState<'new' | PurchasePayment | null>(null);
  const [draft, setDraft] = useState(emptyPurchasePayment);
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [voucher, setVoucher] = useState<PurchasePayment | null>(null);
  const [statePayment, setStatePayment] = useState<PurchasePayment | null>(null);
  const [partial, setPartial] = useState(false);
  const [purchaseVersion, setPurchaseVersion] = useState('');
  const add = usePurchaseOperation('record_payment');
  const edit = usePurchaseOperation('update_purchase_payment');
  const state = usePurchaseOperation(
    statePayment?.is_void ? 'restore_purchase_payment' : 'void_purchase_payment',
  );
  const busy = add.isPending || edit.isPending || state.isPending;
  const balance = Number(purchase.balance_due);
  const canPay =
    enabled &&
    !purchase.is_deleted &&
    !purchase.archived_at &&
    can('purchases.pay') &&
    ['ordered', 'partially_received', 'received'].includes(purchase.status) &&
    balance > 0;
  function start(payment: 'new' | PurchasePayment) {
    setEditor(payment);
    setPurchaseVersion(purchase.updated_at);
    setStatePayment(null);
    setError('');
    setReason('');
    setPartial(false);
    setDraft(
      payment === 'new'
        ? { ...emptyPurchasePayment(), amount: String(balance) }
        : {
            amount: String(payment.amount),
            account_id: payment.account_id,
            payment_date: payment.payment_date,
            payment_method: payment.payment_method,
            reference_number: payment.reference_number || '',
            notes: payment.notes || '',
          },
    );
  }
  async function submit() {
    if (!editor) return;
    setError('');
    try {
      const value = editor === 'new' && !partial ? { ...draft, amount: String(balance) } : draft;
      const checked = checkedPurchasePayment(
        value,
        balance + (editor === 'new' ? 0 : Number(editor.amount)),
      );
      if (editor !== 'new' && (reason.trim().length < 3 || reason.trim().length > 500))
        throw new Error('Enter a correction reason (3–500 characters).');
      let payload: Record<string, unknown> = {
        ...checked,
        purchase_id: purchase.id,
        payment_type: 'purchase_payment',
        ...(editor === 'new'
          ? { pay_remaining: !partial, expected_purchase_updated_at: purchaseVersion }
          : {
              payment_id: editor.id,
              expected_updated_at: editor.updated_at,
              reason: reason.trim(),
            }),
      };
      const send = () =>
        (editor === 'new' ? add : edit).mutateAsync({
          ...payload,
          request_key: intent.key('payment', payload),
        });
      try {
        await send();
      } catch (e) {
        if (
          !(e instanceof Error) ||
          !e.message.startsWith('POSSIBLE_DUPLICATE:') ||
          !(await confirm({
            title: 'Matching supplier payment found',
            description: 'Review payment history. Confirm only if this is another actual payment.',
            confirmText: 'Confirm another payment',
            variant: 'warning',
          }))
        )
          throw e;
        payload = { ...payload, duplicate_confirmed: true };
        await send();
      }
      intent.complete('payment');
      setEditor(null);
      toast.success(editor === 'new' ? 'Supplier payment recorded' : 'Payment corrected');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Payment could not be saved');
    }
  }
  async function changeState() {
    if (!statePayment) return;
    setError('');
    try {
      if (reason.trim().length < 3 || reason.trim().length > 500)
        throw new Error('Enter a reason (3–500 characters).');
      if (
        !(await confirm({
          title: statePayment.is_void ? 'Restore supplier payment?' : 'Void supplier payment?',
          description: `${saleMoney(statePayment.amount)} will ${statePayment.is_void ? 'reduce' : 'increase'} the recorded account balance. The payment history is retained.`,
          confirmText: statePayment.is_void ? 'Restore payment' : 'Void payment',
          variant: 'warning',
        }))
      )
        return;
      const payload = {
        purchase_id: purchase.id,
        payment_id: statePayment.id,
        expected_updated_at: statePayment.updated_at,
        reason: reason.trim(),
      };
      await state.mutateAsync({ ...payload, request_key: intent.key('state', payload) });
      intent.complete('state');
      setStatePayment(null);
      toast.success('Payment updated');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Payment could not be updated');
    }
  }
  return (
    <section className="space-y-4 rounded-2xl border border-border bg-card p-4 sm:p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-semibold">Supplier payments</h2>
          <p className="text-xs text-muted-foreground">
            Review recorded payments before adding another installment.
          </p>
        </div>
        {canPay && (
          <button
            className="min-h-11 rounded-xl bg-primary px-4 text-sm font-semibold text-primary-foreground"
            onClick={() => start('new')}
          >
            Record payment
          </button>
        )}
      </div>
      {payments.isLoading && (
        <p className="text-sm text-muted-foreground">Loading payment history…</p>
      )}
      {payments.error && (
        <p role="alert" className="text-sm text-destructive">
          {payments.error.message}{' '}
          <button className="underline" onClick={() => void payments.refetch()}>
            Retry
          </button>
        </p>
      )}
      {!payments.isLoading && !payments.error && payments.data?.length === 0 && (
        <p className="rounded-xl bg-muted/40 p-4 text-sm text-muted-foreground">
          No supplier payments recorded.
        </p>
      )}
      {payments.data?.map((pay) => (
        <article key={pay.id} className="rounded-xl border border-border p-3">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <p className="font-semibold">
                {saleMoney(pay.amount)}{' '}
                {pay.is_void && <span className="ml-2 text-xs text-muted-foreground">Voided</span>}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {pay.payment_date} · {saleStatusLabel(pay.payment_method)} ·{' '}
                {pay.cash_accounts?.name || 'Recorded account'}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                Recorded by{' '}
                {people.data?.find((person) => person.id === pay.created_by)?.full_name ||
                  'Recorded user'}
                {pay.updated_by ? ' · Updated' : ''}
              </p>
              {pay.reference_number && (
                <p className="mt-1 break-all text-xs">Reference: {pay.reference_number}</p>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {!pay.is_void && (
                <button
                  onClick={() => setVoucher(pay)}
                  className="min-h-11 rounded-lg border border-border px-3 text-xs"
                >
                  Payment voucher
                </button>
              )}
              {enabled && can('purchases.correct') && (
                <>
                  {!pay.is_void && (
                    <button
                      onClick={() => start(pay)}
                      className="min-h-11 rounded-lg border border-border px-3 text-xs"
                    >
                      Edit payment
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setStatePayment(pay);
                      setEditor(null);
                      setReason('');
                      setError('');
                    }}
                    className="min-h-11 rounded-lg border border-border px-3 text-xs"
                  >
                    {pay.is_void ? 'Restore' : 'Void'}
                  </button>
                </>
              )}
            </div>
          </div>
        </article>
      ))}
      <WorkModalShell
        open={!!editor || !!statePayment}
        onClose={() => {
          setEditor(null);
          setStatePayment(null);
        }}
        title={
          statePayment
            ? 'Update supplier payment'
            : editor === 'new'
              ? 'Record supplier payment'
              : 'Correct supplier payment'
        }
        busy={busy}
        dirty={!!editor || !!statePayment}
      >
        <div className="mb-4">
          <h2 className="text-lg font-semibold">
            {statePayment
              ? statePayment.is_void
                ? 'Restore payment'
                : 'Void payment'
              : editor === 'new'
                ? 'Record supplier payment'
                : 'Correct supplier payment'}
          </h2>
          <p className="text-sm text-muted-foreground">
            {purchase.purchase_number} · Balance {saleMoney(balance)}
          </p>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void (statePayment ? changeState() : submit());
          }}
          className="min-h-0 space-y-4 overflow-y-auto"
        >
          {editor === 'new' && (
            <fieldset disabled={busy} className="flex flex-wrap gap-4 text-sm">
              <label className="flex min-h-11 items-center gap-2">
                <Input
                  type="radio"
                  checked={!partial}
                  onChange={() => {
                    setPartial(false);
                    setDraft({ ...draft, amount: String(balance) });
                  }}
                  helpLabel="Pay balance"
                />{' '}
                Pay balance
              </label>
              <label className="flex min-h-11 items-center gap-2">
                <Input
                  type="radio"
                  checked={partial}
                  onChange={() => setPartial(true)}
                  helpLabel="Partial payment"
                /> Partial
                payment
              </label>
            </fieldset>
          )}
          {!!editor && (
            <PurchasePaymentFields
              amountReadOnly={editor === 'new' && !partial}
              value={draft}
              onChange={setDraft}
              disabled={busy}
            />
          )}
          {(statePayment || editor !== 'new') && (
            <label className="block space-y-1 text-sm" htmlFor="purchase-payment-reason">
              <span>Reason *</span>
              <Textarea
                id="purchase-payment-reason"
                required
                minLength={3}
                maxLength={500}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                disabled={busy}
                className={purchaseInputClass}
                helpLabel="Reason *"
              />
            </label>
          )}
          {error && (
            <p role="alert" className="rounded-xl bg-destructive/10 p-3 text-sm text-destructive">
              {error}
              <button
                type="button"
                disabled={busy}
                onClick={async () => {
                  try {
                    const prior = await intent.recover(statePayment ? 'state' : 'payment');
                    if (prior) {
                      setEditor(null);
                      setStatePayment(null);
                      await refreshSaleConsumers(client);
                      toast.info('The previous request completed. Payment history refreshed.');
                    } else toast.info('No completed request found. Retry this payment form.');
                  } catch (e) {
                    setError(
                      e instanceof Error ? e.message : 'Could not check the previous request',
                    );
                  }
                }}
                className="ml-2 min-h-11 underline"
              >
                Check previous request
              </button>
            </p>
          )}
          <div className="flex justify-end gap-2 pb-1">
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setEditor(null);
                setStatePayment(null);
              }}
              className="min-h-11 rounded-xl border border-border px-4 text-sm"
            >
              Cancel
            </button>
            <button
              disabled={busy}
              className="min-h-11 rounded-xl bg-primary px-4 text-sm font-semibold text-primary-foreground disabled:opacity-50"
            >
              {busy ? 'Saving…' : 'Confirm payment'}
            </button>
          </div>
        </form>
      </WorkModalShell>
      {voucher && (
        <ReceiptPreviewModal
          isOpen
          onClose={() => setVoucher(null)}
          paymentData={{
            receipt_number: `PAY-${voucher.id.slice(0, 8).toUpperCase()}`,
            payment_type: 'purchase_payment',
            payment_date: voucher.payment_date,
            party_name: purchase.suppliers?.name || 'Supplier',
            amount: Number(voucher.amount),
            payment_method: voucher.payment_method,
            particular: `Supplier payment for ${purchase.purchase_number}`,
            reference_number: voucher.reference_number || undefined,
            notes: voucher.notes,
          }}
        />
      )}
    </section>
  );
}
